// Surface — platform-level resizable pane primitive.
//
// Every multi-pane page in the terminal renders a <Surface>. A Surface owns:
//   - n <Pane>s with auto-inserted Separators between them
//   - layout persistence (localStorage now; server sync via useLayoutPersistence)
//   - a small reset affordance in its chrome (when chrome is mounted)
//
// Stack:
//   <Surface id="terminal" direction="horizontal">
//     <Pane id="left"  title="MY CONTRACTS" defaultSize={62} minSize={20}>…</Pane>
//     <Pane id="right" title="PIPELINE"     defaultSize={38} minSize={20}>…</Pane>
//   </Surface>
//
// Nest Surfaces inside a Pane for N-quadrant layouts (e.g. /terminal's 4-pane).
//
// Visual + interaction upgrades live in tokens.css (the [data-separator] block):
//   - 4px visible line at rest; widens to 6px on hover/focus; grip dots appear
//   - 20px hit zone via ::before { inset: -8px }
//   - drag-active state via :active (pointer is captured during drag)
//   - keyboard resize is handled by react-resizable-panels (Arrow / Home / End)
//   - page-wide cursor lock during drag is handled by react-resizable-panels
//
// Mobile (<768px): tokens.css flattens the stack — separators hide, panes
// stack vertically. No prop wiring needed; container-driven.

import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useState,
} from 'react';
import {
  Group,
  type GroupImperativeHandle,
  Panel,
  type PanelImperativeHandle,
  Separator,
  useDefaultLayout,
  useGroupRef,
  usePanelRef,
} from 'react-resizable-panels';

// Module-scoped registry of mounted Surface groupRefs, keyed by Surface id.
// SurfaceReset reads from this to reset layouts in-place without a page reload.
const surfaceRegistry = new Map<string, { current: GroupImperativeHandle | null }>();

// ── Pane ────────────────────────────────────────────────────────────────────
// A <Pane> wraps react-resizable-panels' <Panel>. It optionally renders a
// 24px header strip (title + actions + collapse chevron) above its children
// so the pane structure is self-evident — this is the "more obvious" half of
// the founder's ask. Pass `title` to get the header; omit it for chromeless.

export interface PaneProps {
  /** Stable id (required when the parent Surface persists). */
  id: string;
  /** Default size as a percentage of the parent Surface (0..100). */
  defaultSize?: number;
  /** Minimum size as a percentage of the parent Surface (0..100). */
  minSize?: number;
  /** Maximum size as a percentage of the parent Surface (0..100). */
  maxSize?: number;
  /**
   * If true, the pane collapses to 0% when dragged below `minSize`, and a
   * chevron button in the header expands it back to `defaultSize`.
   */
  collapsible?: boolean;
  /** Title shown in the pane header. Omit for chromeless panes. */
  title?: string;
  /** Optional subtitle / right-aligned meta in the header. */
  sub?: ReactNode;
  /** Optional action node rendered between title and the collapse chevron. */
  actions?: ReactNode;
  children?: ReactNode;
}

export function Pane({
  id,
  defaultSize,
  minSize,
  maxSize,
  collapsible,
  title,
  sub,
  actions,
  children,
}: PaneProps) {
  const panelRef = usePanelRef();
  // Track collapse state by listening to onResize so a drag-collapse and an
  // imperative collapse both keep the chevron glyph in sync.
  const [collapsed, setCollapsed] = useState(false);

  // CRITICAL: Panel from react-resizable-panels does NOT honor display/
  // flex-direction/flex-wrap/overflow style overrides on its own element
  // (per the library's API docs). The flex column for [header + body] must
  // therefore live on a wrapper div INSIDE the Panel, not on the Panel
  // itself. Setting display:flex on Panel via `style` is silently dropped.
  // react-resizable-panels v4 interprets numeric sizes as PIXELS. Our wrapper
  // contract is percent (0..100), so stringify with `%`. See Resizable.tsx
  // for the full rationale.
  const pct = (n: number | undefined): string | undefined =>
    typeof n === 'number' ? `${n}%` : undefined;
  return (
    <Panel
      id={id}
      defaultSize={pct(defaultSize)}
      minSize={pct(minSize)}
      maxSize={pct(maxSize)}
      collapsible={collapsible}
      panelRef={panelRef}
      onResize={collapsible ? (size) => setCollapsed(size.asPercentage < 1) : undefined}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {title !== undefined && (
          <header className="pane-h">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ whiteSpace: 'nowrap' }}>{title}</span>
              {sub !== undefined && sub !== null && (
                <span
                  className="meta mono"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {sub}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {actions}
              {collapsible && <CollapseToggle panelRef={panelRef} collapsed={collapsed} />}
            </div>
          </header>
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {children}
        </div>
      </div>
    </Panel>
  );
}

function CollapseToggle({
  panelRef,
  collapsed,
}: {
  panelRef: { current: PanelImperativeHandle | null };
  collapsed: boolean;
}) {
  return (
    <button
      type="button"
      className="btn xs gh pane-collapse"
      data-collapsed={collapsed}
      aria-label={collapsed ? 'Expand pane' : 'Collapse pane'}
      aria-expanded={!collapsed}
      onClick={() => {
        const p = panelRef.current;
        if (!p) return;
        if (p.isCollapsed()) p.expand();
        else p.collapse();
      }}
    >
      {collapsed ? '+' : '−'}
    </button>
  );
}

// ── Surface ─────────────────────────────────────────────────────────────────
// A <Surface> renders a horizontal or vertical Group of Panes with Separators
// auto-inserted between them. Pass `persist` (default true) to read/write
// the layout to localStorage under `layout.<id>`.

export interface SurfaceProps {
  /** Stable id (used as the persistence key). */
  id: string;
  /** Direction of resize. */
  direction?: 'horizontal' | 'vertical';
  /**
   * Persist the layout to localStorage. Default `true`. Pass `false` for
   * surfaces whose sizes are computed on the fly (rare).
   */
  persist?: boolean;
  /** Pass-through style on the Group root. */
  style?: React.CSSProperties;
  /** className on the Group root. */
  className?: string;
  /** <Pane> children. Separators are auto-inserted between them. */
  children: ReactNode;
}

export function Surface({
  id,
  direction = 'horizontal',
  persist = true,
  style,
  className,
  children,
}: SurfaceProps) {
  const groupRef = useGroupRef();
  const persistKey = `layout.${id}`;
  // useDefaultLayout always runs; when persist=false we just don't use its
  // outputs. (Hooks must run unconditionally — useId fills in.)
  const fallbackId = useId();
  const layout = useDefaultLayout({ id: persist ? persistKey : fallbackId });

  // Register/unregister so SurfaceReset can reach this Surface without a
  // context. Module-scoped Map; idempotent re-registration is safe.
  useEffect(() => {
    surfaceRegistry.set(id, groupRef);
    return () => {
      if (surfaceRegistry.get(id) === groupRef) surfaceRegistry.delete(id);
    };
  }, [id, groupRef]);

  // Filter children to only valid <Pane> elements; auto-insert <Separator>.
  const panes = Children.toArray(children).filter(isValidElement) as ReactElement<PaneProps>[];
  const composed: ReactNode[] = [];
  panes.forEach((p, i) => {
    if (i > 0) {
      composed.push(<Separator key={`sep-${i}`} />);
    }
    // Re-key children so React doesn't reuse fibers across structure changes.
    composed.push(cloneElement(p, { key: p.props.id ?? `pane-${i}` }));
  });

  return (
    <Group
      id={id}
      orientation={direction}
      groupRef={groupRef}
      defaultLayout={persist ? layout.defaultLayout : undefined}
      onLayoutChanged={persist ? layout.onLayoutChanged : undefined}
      style={{ flex: 1, minHeight: 0, minWidth: 0, ...style }}
      className={className}
      data-surface
    >
      {composed}
    </Group>
  );
}

// ── SurfaceReset ────────────────────────────────────────────────────────────
// Tiny button that resets a Surface (or a set of them) to defaults. Wired to
// each Surface's Group via setLayout({}) which the library treats as "use the
// configured defaultSize for each panel."
//
// Usage:
//   const surfaces = ['terminal.h', 'terminal.lv', 'terminal.rv'];
//   <SurfaceReset surfaceIds={surfaces} />
//
// Implementation note: rather than thread refs through every Surface, this
// clears the persisted layout keys and reloads the surfaces by dispatching a
// custom storage event. Simpler than a context.

export interface SurfaceResetProps {
  /** Surface ids to reset (without the `layout.` prefix). */
  surfaceIds: string[];
  className?: string;
  label?: string;
}

export function SurfaceReset({
  surfaceIds,
  className = 'btn xs gh',
  label = 'RESET LAYOUT',
}: SurfaceResetProps) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        for (const id of surfaceIds) {
          try {
            localStorage.removeItem(`layout.${id}`);
          } catch {
            // localStorage may be unavailable (Safari private mode, etc.)
          }
          // Reset layout in place via the live groupRef. Passing an empty
          // map tells the library to fall back to each Panel's defaultSize.
          surfaceRegistry.get(id)?.current?.setLayout({});
        }
      }}
    >
      {label}
    </button>
  );
}
